import groupBy from "lodash/groupBy"
import mean from "lodash/mean"
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts"

import { dataByPlatform } from "@/constants/platform/data-by-platform"
import type { Platform } from "@/constants/platform/platform"
import type { ParsedReviewWithCompanyId } from "@/db/queries/parsed-review"

type PlatformStat = {
  platform: Platform
  count: number
  avgRating: number | null
}

type YearData = {
  year: string
  count: number
  platforms: PlatformStat[]
}

function getYearData(reviews: ParsedReviewWithCompanyId[]) {
  // Filter out reviews without dates or that are duplicates
  const validReviews = reviews.filter((r) => r.date && !r.duplicate_type)

  // Group by year
  const byYear = groupBy(validReviews, (r) => r.date!.slice(0, 4))

  // Transform to chart data
  const yearData: YearData[] = Object.entries(byYear)
    .map(([year, yearReviews]) => {
      // Group by platform within year
      const byPlatform = groupBy(yearReviews, "platform")

      const platforms: PlatformStat[] = Object.entries(byPlatform)
        .map(([platform, platformReviews]) => {
          const ratings = platformReviews
            .map((r) => r.rating)
            .filter((r): r is number => r !== null)
          const avgRating = ratings.length > 0 ? mean(ratings) : null

          return {
            platform: platform as Platform,
            count: platformReviews.length,
            avgRating,
          }
        })
        .sort((a, b) => b.count - a.count)

      return {
        year,
        count: yearReviews.length,
        platforms,
      }
    })
    .sort((a, b) => a.year.localeCompare(b.year))

  return yearData
}

function CustomTooltip({
  active: isActive,
  payload,
}: {
  // eslint-disable-next-line english-naming/boolean-prefix -- Recharts clones the tooltip content with its own `active` prop.
  active?: boolean
  payload?: Array<{ payload: YearData }>
}) {
  if (!isActive || !payload?.[0]) return null

  const data = payload[0].payload
  return (
    <div className="bg-popover rounded-lg border px-4 py-3 shadow-lg">
      <p className="text-sm font-semibold">{data.year}</p>
      <p className="text-muted-foreground mb-2 text-sm">{data.count} reviews</p>
      <div className="space-y-1">
        {data.platforms.map((ps) => (
          <div key={ps.platform} className="flex items-center gap-2 text-sm">
            {dataByPlatform[ps.platform]?.favicon && (
              <img
                src={dataByPlatform[ps.platform].favicon!}
                alt={dataByPlatform[ps.platform].title}
                width={16}
                height={16}
                className="rounded-sm"
                loading="lazy"
              />
            )}
            <span className="text-muted-foreground">
              {dataByPlatform[ps.platform]?.title}
            </span>
            <span className="ml-auto font-medium">
              {ps.count}
              {ps.avgRating !== null && (
                <span className="text-muted-foreground ml-1">
                  ({ps.avgRating.toFixed(1)}★)
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function CustomXAxisTick({
  x,
  y,
  payload,
  yearData,
}: {
  x: number
  y: number
  payload: { value: string }
  yearData: YearData[]
}) {
  const data = yearData.find((d) => d.year === payload.value)
  if (!data) return null

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="hsl(var(--foreground))"
        fontWeight={600}
      >
        {payload.value}
      </text>
    </g>
  )
}

export function ReviewHistorySection({
  reviews,
}: {
  reviews: ParsedReviewWithCompanyId[]
}) {
  const yearData = getYearData(reviews)

  if (yearData.length === 0) {
    return (
      <p className="text-muted-foreground text-center">
        No review data available
      </p>
    )
  }

  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={yearData}
          margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
        >
          <XAxis
            dataKey="year"
            tickLine={false}
            axisLine={false}
            tick={(props) => <CustomXAxisTick {...props} yearData={yearData} />}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]}>
            <LabelList
              dataKey="count"
              position="top"
              offset={10}
              fill="hsl(var(--foreground))"
              fontWeight={600}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
